import { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useActiveFund } from '../../hooks/useActiveFund';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';
import NarrativePanel from '../../components/analytics/NarrativePanel';
import FunnelConversionView from '../../components/analytics/FunnelConversionView';
import SectorAnalysisView from '../../components/analytics/SectorAnalysisView';
import CountryAnalysisView from '../../components/analytics/CountryAnalysisView';
import TicketSizeAnalysisView from '../../components/analytics/TicketSizeAnalysisView';
import WomenLedView from '../../components/analytics/WomenLedView';
import ThresholdSensitivityView from '../../components/analytics/ThresholdSensitivityView';

// Roles allowed to see the Threshold Sensitivity tab —
// this is a management-level simulation tool, not analyst-level
const THRESHOLD_ROLES = ['head_of_pipeline', 'cio', 'md', 'admin'];

const BASE_TABS = [
  { id: 'funnel', label: 'Funnel' },
  { id: 'sectors', label: 'Sectors' },
  { id: 'countries', label: 'Countries' },
  { id: 'ticket-size', label: 'Ticket Size' },
  { id: 'women-led', label: 'Women-Led' },
];

export default function AnalyticsPage() {
  const { userProfile, authReady } = useAuth();
  const { activeFundId } = useActiveFund();
  const [deals, setDeals] = useState([]);
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('funnel');

  const userRoles = userProfile?.roles || (userProfile?.role ? [userProfile.role] : []);
  const canSeeThreshold = THRESHOLD_ROLES.some(r => userRoles.includes(r));

  const tabs = canSeeThreshold
    ? [...BASE_TABS, { id: 'threshold', label: 'Threshold' }]
    : BASE_TABS;

  useEffect(() => {
    if (!authReady || !activeFundId) { setLoading(false); return; }
    setLoading(true);
    const run = async () => {
      const [fundSnap, dealsSnap] = await Promise.all([
        getDocs(query(collection(db, 'funds'), where('__name__', '==', activeFundId))),
        getDocs(query(collection(db, 'deals'), where('fund_id', '==', activeFundId))),
      ]);
      setFund(fundSnap.docs[0] ? { id: fundSnap.docs[0].id, ...fundSnap.docs[0].data() } : null);
      setDeals(dealsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };
    run().catch(err => { console.error('Analytics load error:', err); setLoading(false); });
  }, [authReady, activeFundId]);

  // If active fund changes, reset to the funnel tab
  useEffect(() => { setActiveTab('funnel'); }, [activeFundId]);

  if (!activeFundId) {
    return (
      <PortalLayout>
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <h3>No Fund Selected</h3>
          <p>Select a fund to view analytics.</p>
        </div>
      </PortalLayout>
    );
  }

  if (loading) return <PortalLayout><LoadingSpinner /></PortalLayout>;

  return (
    <PortalLayout>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
          Pipeline Analytics
        </h1>
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
          {deals.filter(d => d.status === 'active').length} active deals · {fund?.name}
        </p>
      </div>

      {/* AI Narrative Panel — full width, on-demand only */}
      <div style={{ marginBottom: 24 }}>
        <NarrativePanel fund={fund} />
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: 20 }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14,
              fontWeight: activeTab === t.id ? 600 : 400,
              color: activeTab === t.id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              borderBottom: activeTab === t.id ? '2px solid var(--color-accent)' : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {deals.length === 0 && activeTab !== 'threshold' ? (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <h3>No deals yet</h3>
          <p>Analytics will appear once deals have been submitted for this fund.</p>
        </div>
      ) : (
        <>
          {activeTab === 'funnel' && <FunnelConversionView deals={deals} fund={fund} />}
          {activeTab === 'sectors' && <SectorAnalysisView deals={deals} fund={fund} />}
          {activeTab === 'countries' && <CountryAnalysisView deals={deals} fund={fund} />}
          {activeTab === 'ticket-size' && <TicketSizeAnalysisView deals={deals} fund={fund} />}
          {activeTab === 'women-led' && <WomenLedView deals={deals} fund={fund} />}
          {activeTab === 'threshold' && canSeeThreshold && (
            <ThresholdSensitivityView deals={deals} activeFundId={activeFundId} />
          )}
        </>
      )}
    </PortalLayout>
  );
}
